import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import Layout from './components/Layout';
import CandidateProtectedRoute from './components/CandidateProtectedRoute';
import PermissionProtectedRoute from './components/PermissionProtectedRoute';
import Login from './pages/Login';
import CandidateLogin from './pages/CandidateLogin';
import CandidateDashboard from './pages/CandidateDashboard';
import CandidateApplicationStatus from './pages/CandidateApplicationStatus';
import CandidateInformation from './pages/CandidateInformation';
import Careers from './pages/Careers';
import JobDetails from './pages/JobDetails';
import JobApplication from './pages/JobApplication';
import SavedJobs from './pages/SavedJobs';
import LoadingSpinner from './components/LoadingSpinner';
import ChangePassword from './pages/ChangePassword';
import Profile from './pages/Profile';
import SessionTimeoutModal from './modals/SessionTimeOutModal';
import SsoCallback from './pages/SsoCallback';

const Employees = React.lazy(() => import('./pages/Employees'));
const Leaves = React.lazy(() => import('./pages/Leaves'));
const Attendance = React.lazy(() => import('./pages/Attendance'));
const Payroll = React.lazy(() => import('./pages/Payroll'));
const Schedule = React.lazy(() => import('./pages/Schedule'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Roles = React.lazy(() => import('./pages/Roles'));
const FaceDetection = React.lazy(() => import('./pages/FaceDetection'));
const EmployeeAttendance = React.lazy(() => import('./pages/EmployeeAttendance'));
const JobDescriptions = React.lazy(() => import('./pages/JobDescriptions'));
const JobDescriptionView = React.lazy(() => import('./pages/JobDescriptionView'));
const JobPosting = React.lazy(() => import('./pages/JobPosting'));
const JobApplicationsManagement = React.lazy(() => import('./pages/JobApplicationsManagement'));
const AvailabilityDemo = React.lazy(() => import('./pages/ApplicantAvailability'));
const JobApplicationView = React.lazy(() => import('./pages/JobApplicationView'));

const App: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <>
      <Toaster position="top-right" />
      {isAuthenticated && <SessionTimeoutModal />}
      <Routes>
        {/* Public routes - accessible without authentication */}
        <Route path="/" element={<Navigate to="/careers" replace />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/saved-jobs" element={<SavedJobs />} />
        <Route path="/job/:id" element={<JobDetails />} />
        <Route path="/job/:id/apply" element={<JobApplication />} />
        <Route 
          path="/job-description/:id" 
          element={
            <React.Suspense fallback={<LoadingSpinner fullScreen />}>
              <JobDescriptionView />
            </React.Suspense>
          } 
        />
        <Route path="/login" element={<Login />} />
        <Route path="/login/employee" element={<Navigate to="/login" replace />} />
        <Route path="/sso/callback" element={<SsoCallback />} />
        <Route path="/candidate-login" element={<CandidateLogin />} />
        <Route
          path="/availability/:id"
          element={
            <React.Suspense fallback={<LoadingSpinner fullScreen />}>
              <AvailabilityDemo />
            </React.Suspense>
          }
        />

        {/* Candidate routes - accessible after candidate login */}
        <Route path="/candidate" element={<Navigate to="/candidate/dashboard" replace />} />
        <Route
          path="/candidate/dashboard"
          element={
            <CandidateProtectedRoute>
              <CandidateDashboard />
            </CandidateProtectedRoute>
          }
        />
        <Route
          path="/candidate/application-status"
          element={
            <CandidateProtectedRoute>
              <CandidateApplicationStatus />
            </CandidateProtectedRoute>
          }
        />
        <Route
          path="/candidate/information"
          element={
            <CandidateProtectedRoute>
              <CandidateInformation />
            </CandidateProtectedRoute>
          }
        />

        {/* Protected routes - only accessible when authenticated and with proper permissions */}
        <Route
          path="/roles"
          element={
            <PermissionProtectedRoute requiredScreenCode="roles_management" adminOnly>
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <Roles />
                </React.Suspense>
              </Layout>
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/employees"
          element={
            <PermissionProtectedRoute requiredScreenCodes={['employees_management', 'employees_basic']}>
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <Employees />
                </React.Suspense>
              </Layout>
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/face-detection"
          element={
            <PermissionProtectedRoute requiredScreenCode="faceid_setup">
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <FaceDetection />
                </React.Suspense>
              </Layout>
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/employee-attendance"
          element={
            <PermissionProtectedRoute requiredScreenCode="attendance_management">
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <EmployeeAttendance />
                </React.Suspense>
              </Layout>
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/leaves"
          element={
            <PermissionProtectedRoute requiredScreenCode="leaves_management">
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <Leaves />
                </React.Suspense>
              </Layout>
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/attendance"
          element={
            <PermissionProtectedRoute requiredScreenCode="attendance_management">
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <Attendance />
                </React.Suspense>
              </Layout>
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/payroll"
          element={
            <PermissionProtectedRoute requiredScreenCode="payroll_management">
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <Payroll />
                </React.Suspense>
              </Layout>
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/schedule"
          element={
            <PermissionProtectedRoute requiredScreenCode="schedules_management">
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <Schedule />
                </React.Suspense>
              </Layout>
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/job-descriptions"
          element={
            <PermissionProtectedRoute requiredScreenCode="job_descriptions_management">
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <JobDescriptions />
                </React.Suspense>
              </Layout>
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/job-postings"
          element={
            <PermissionProtectedRoute requiredScreenCode="job_postings_management">
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <JobPosting />
                </React.Suspense>
              </Layout>
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/job-applications-management"
          element={
            <PermissionProtectedRoute requiredScreenCode="job_applications_management">
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <JobApplicationsManagement />
                </React.Suspense>
              </Layout>
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/job-applications-management/:id"
          element={
            <PermissionProtectedRoute requiredScreenCode="job_applications_management">
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <JobApplicationView />
                </React.Suspense>
              </Layout>
            </PermissionProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            isAuthenticated ? (
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <Settings />
                </React.Suspense>
              </Layout>
            ) : (
              <Navigate to="/careers" replace />
            )
          }
        />
        <Route
          path="/change-password"
          element={
            isAuthenticated ? (
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <ChangePassword />
                </React.Suspense>
              </Layout>
            ) : (
              <Navigate to="/careers" replace />
            )
          }
        />
        <Route
          path="/profile"
          element={
            isAuthenticated ? (
              <Layout>
                <React.Suspense fallback={<LoadingSpinner fullScreen />}>
                  <Profile />
                </React.Suspense>
              </Layout>
            ) : (
              <Navigate to="/careers" replace />
            )
          }
        />

        {/* Catch-all: careers for guests, profile for authenticated users */}
        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? "/profile" : "/careers"} replace />}
        />
      </Routes>
    </>
  );
};

export default App; 